import { useEffect, useState } from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import {
  RelatedSummary,
  RelatedDetail,
  RelatedDetailItem,
  fetchRelatedDetail,
} from "../../services/duplicateCandidateService";

const useStyles = makeStyles({
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    alignItems: "center",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontSize: "0.72rem",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "8px",
    paddingRight: "8px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("999px"),
  },
  chipNum: { fontWeight: 700, color: BRAND.purple },
  ap: {
    color: BRAND.muted,
    fontSize: "0.75rem",
    marginTop: "4px",
  },
  // Variante für die lila Sieger-Karte (CountChips sitzt dort auf BRAND.purple).
  // Muss nach `ap` stehen — siehe Reihenfolge-Hinweis in ContactMergePage.
  apOnDark: { color: "rgba(255,255,255,0.82)" },
  detailWrap: {
    marginTop: "8px",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  sectionTitle: {
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#5d5664",
    marginBottom: "2px",
  },
  item: {
    fontSize: "0.82rem",
    color: BRAND.ink,
    paddingTop: "1px",
    paddingBottom: "1px",
  },
  itemSub: { color: BRAND.muted },
  loading: { color: BRAND.muted, fontStyle: "italic", fontSize: "0.8rem" },
});

const COUNT_FIELDS: Array<{
  key: keyof RelatedSummary;
  label: string;
  always?: boolean;
}> = [
  { key: "addresses", label: "Adressen" },
  { key: "communications", label: "Kommunikation" },
  { key: "bankAccounts", label: "Bank" },
  { key: "relations", label: "Beziehungen" },
  { key: "vorgaenge", label: "Vorgänge" },
  { key: "documents", label: "Dokumente" },
  { key: "damages", label: "Schäden" },
  { key: "contracts", label: "Verträge", always: true },
  { key: "risks", label: "Risiken", always: true },
];

/**
 * Count badges + Ansprechpartner-of line for a contact's attached records.
 * `onDark` schaltet die Textfarbe auf Weiß — die Sieger-Karte im Merge ist lila.
 */
export const CountChips = ({ summary, onDark = false }: { summary?: RelatedSummary; onDark?: boolean }) => {
  const styles = useStyles();
  if (!summary) return null;
  return (
    <div>
      <div className={styles.chips}>
        {COUNT_FIELDS.map((f) => {
          const n = (summary[f.key] as number) ?? 0;
          if (!n && !f.always) return null;
          return (
            <span key={f.key} className={styles.chip}>
              {f.label}
              <span className={styles.chipNum}>{n}</span>
            </span>
          );
        })}
      </div>
      {summary.ansprechpartnerOf && summary.ansprechpartnerOf.length > 0 && (
        <div className={mergeClasses(styles.ap, onDark && styles.apOnDark)}>
          Ansprechpartner bei:{" "}
          {summary.ansprechpartnerOf
            .map(
              (a) =>
                a.companyName + (a.function ? ` (${a.function})` : ""),
            )
            .join(", ")}
        </div>
      )}
    </div>
  );
};

/** Lazy-loaded full record lists for one contact (rendered when expanded). */
export const RelatedDetailView = ({ contactId }: { contactId: number }) => {
  const styles = useStyles();
  const [detail, setDetail] = useState<RelatedDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    setError(null);
    fetchRelatedDetail(contactId)
      .then((d) => {
        if (!aborted) setDetail(d);
      })
      .catch((e) => {
        if (!aborted) setError(String(e?.message ?? e));
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [contactId]);

  if (loading)
    return (
      <div className={styles.loading}>Verknüpfte Datensätze werden geladen…</div>
    );
  if (error) return <div className={styles.loading}>Fehler: {error}</div>;
  if (!detail) return null;

  const sections: Array<{ label: string; items: RelatedDetailItem[] }> = [
    { label: "Adressen", items: detail.addresses },
    { label: "Kommunikation", items: detail.communications },
    { label: "Bank", items: detail.bankAccounts },
    { label: "Beziehungen", items: detail.relations },
    { label: "Kundenverbindungen", items: detail.kundenverbindungen },
    { label: "Vorgänge", items: detail.vorgaenge },
    { label: "Dokumente", items: detail.documents },
    { label: "Schäden", items: detail.damages },
    { label: "Verträge", items: detail.contracts },
    { label: "Risiken", items: detail.risks },
  ];
  const nonEmpty = sections.filter((s) => s.items.length > 0);
  if (nonEmpty.length === 0)
    return <div className={styles.loading}>Keine verknüpften Datensätze.</div>;

  return (
    <div className={styles.detailWrap}>
      {nonEmpty.map((s) => (
        <div key={s.label}>
          <div className={styles.sectionTitle}>
            {s.label} ({s.items.length})
          </div>
          {s.items.map((it) => (
            <div key={it.id} className={styles.item}>
              {it.label}
              {it.isDefault ? " ★" : ""}
              {it.sub ? <span className={styles.itemSub}> — {it.sub}</span> : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
